import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { CimaCore, CimaToken, Cloudinary, SessionStorage, Util, WindowReference, XmStore, XmStoreUtil } from 'core/services';
import { CmsCore } from 'store/cms/models';
import { User } from 'store/user/models';
import { CONFIG_TOKEN, CORE_ACCOUNT_URL, StorageToken } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { LocationCollection } from 'store/locations/models/location';
import { StoreAction } from 'store/actions';

@Component({
    selector: 'xm-nav-hamburger',
    styleUrls: ['./hamburger.scss'],
    templateUrl: './hamburger.html',
    standalone: false
})
export class NavHamburgerComponent implements OnInit, OnChanges, OnDestroy {
    public static DELAY_POSITION: number = 300;
    @Input() public hideLearnBuy: boolean;
    // DO NOT DELETE this input variable (triggerOnChanges) check primary.html for usage
    @Input() public triggerOnChanges: boolean;
    @Output() public isMobileNavExpand: EventEmitter<boolean> = new EventEmitter<boolean>();

    public isLoggedIn: boolean = false;
    public isMobileNavOpen: boolean = false;
    public abTestExperience: string;
    public linkedAccounts: LocationRow[] = [];
    public isCBMAccountSelectorEnabled: boolean = false;
    public isOrionAccountSelectorEnabled: boolean = false;
    public isOrionTenant: boolean = false;
    public locationData: LocationCollection;
    public pageData: SiteNav;
    public mobileMenuItems: CmsNavMenuItems[] = [];
    public coreAccountMenuItem: CmsNavMenuItems;
    public isBusinessUser: boolean = false;

    private windowRef: WindowReference;
    private htmlBody: HTMLElement;
    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private cimaToken: CimaToken;
    private cimaCore: CimaCore;
    private config: IXMOptions;
    private sessionStorage: SessionStorage;

    constructor(@Inject(CONFIG_TOKEN) config: IXMOptions, cimaToken: CimaToken, windowRef: WindowReference, xmStore: XmStore, cimaCore: CimaCore, sessionStorage: SessionStorage) {
        Object.assign(this, { config, cimaToken, windowRef, xmStore, cimaCore, sessionStorage });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isOrionTenant = this.sessionStorage.get(StorageToken.X_TENANT_ID) === 'ORION';
        this.isCBMAccountSelectorEnabled = this.config.ENABLE_CBM_ACCOUNT_SELECTOR && !this.isOrionTenant;
        this.isOrionAccountSelectorEnabled = this.config.ENABLE_ORION_ACCOUNT_SELECTOR && this.isOrionTenant;
        this.htmlBody = window.document.getElementsByTagName('body')[0];

        this.subscriptions.push(this.windowRef.breakpoint.subscribe((breakpoint: Breakpoint) => {
            if (this.isMobileNavOpen) {
                if (!breakpoint.isMobile && !breakpoint.isTablet) {
                    this.setStyleMobileView(false);
                } else {
                    this.setStyleMobileView(true);
                }
            }
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User, { returnUndefined: true }), (user: User) => {
            this.isLoggedIn = this.cimaToken.isLoggedIn && Boolean(user && user.displayName);
            this.setCoreAccountMenuItem();
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.pageData = result.nav;
            this.mobileMenuItems = this.cimaCore.isBusinessUser ? this.pageData.cbmMobileMenuItems : this.pageData.mobileMenuItems;
            this.setCoreAccountMenuItem();
            this.setMakePaymentLink();
        }));

        if (this.isBusinessUser && (this.isCBMAccountSelectorEnabled || this.isOrionAccountSelectorEnabled)) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.find<LocationCollection>(StoreAction.GET_SWITCH_LOCATIONS, LocationCollection), (data: LocationCollection) => {
                this.locationData = data;
                const accountList: LocationRow[] = this.locationData?.locationRows ?? [];
                this.linkedAccounts = accountList?.map((account: LocationRow) => {
                    const [address = '', cityStateZip]: string[] = account.location?.split('\n') ?? [];

                    return { ...account, address: address.trim(), cityStateZip: cityStateZip?.trim() ?? '' };
                });
            }));
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public ngOnChanges(): void {
        if (this.isMobileNavOpen) {
            this.toggleMobileNav();
        }
    }

    public toggleMobileNav(): void {
        this.isMobileNavOpen = !this.isMobileNavOpen;
        this.isMobileNavExpand.emit(this.isMobileNavOpen);
        this.setStyleMobileView(this.isMobileNavOpen);
    }

    public generateCmsMediaImage(icon: CmsMediaImage): MediaImageOptions {
        if (!icon) {
            return undefined;
        }

        return { mobile: Cloudinary.generateMediaFromCms(icon) };
    }

    public generateUiParams(paramsString: string): object {
        return Util.getObjectFromQueryString(paramsString);
    }

    private setMakePaymentLink(): void {
        if (this.pageData.makePayment && this.pageData.makePayment.linkDecoration) {
            this.pageData.makePayment.linkDecoration = this.pageData.makePayment.linkDecoration.replace('[PAYMENT_BASE_URL]', this.config.PAYMENT_BASE_URL);
        }
    }

    private setCoreAccountMenuItem(): void {
        const myAccountItems: CmsNavMenuItems[] = this.isLoggedIn ? this.pageData?.myAccount?.children : this.pageData?.myAccount?.unaunthenticatedChildren;
        this.coreAccountMenuItem = this.isBusinessUser ? undefined : myAccountItems?.find((item: CmsNavMenuItems) => item.linkDecoration?.includes(CORE_ACCOUNT_URL));
    }

    private setStyleMobileView(isMobileNav: boolean = false): void {
        if (isMobileNav) {
            this.htmlBody.style['overflow-y'] = 'hidden';
            window.setTimeout(() => {
                this.htmlBody.style.position = 'fixed';
            }, NavHamburgerComponent.DELAY_POSITION);
        } else {
            this.htmlBody.style.removeProperty('overflow-y');
            this.htmlBody.style.removeProperty('position');
        }
    }
}
